import { useState, useRef, useEffect, useCallback } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tag, Flag, Loader2, Plus, FileText, ListChecks } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useNoteTypesQuery } from "@/mutations/noteTypes";
import { cn } from "@/utils";
import * as chatApi from "@/lib/api/chat";
import { useEventListener } from "usehooks-ts";
import {
  MarkdownEditor,
  type MarkdownEditorHandle,
} from "@/components/markdown-editor";
import { Form, FormControl, FormField, FormItem } from "@/components/ui/form";
import { useCreateNoteList, useAddNoteToList } from "@/mutations/noteLists";
import { noteListQueryOptions } from "@/queries/noteLists";
import { useCreateNote } from "@/mutations/notes";

const compactNoteSchema = z.object({
  title: z.string().min(1, "Title is required"),
  content: z.string().optional(),
  type: z.string(),
  dueDate: z.date().optional(),
});

type CompactNoteFormValues = z.infer<typeof compactNoteSchema>;

type FormMode = "note" | "list";

type CompactNoteFormProps = {
  onSubmit: (data: {
    title: string;
    content?: string;
    type?: string;
    dueDate?: Date;
  }) => Promise<{ id: string } | undefined>;
  isSubmitting: boolean;
  defaultDate?: Date;
  autoExpand?: boolean;
  onExpandChange?: (expanded: boolean) => void;
};

export function CompactNoteForm({
  onSubmit,
  isSubmitting,
  defaultDate,
  autoExpand,
  onExpandChange,
}: CompactNoteFormProps) {
  const [isExpanded, setIsExpanded] = useState(autoExpand ?? false);
  const [mode, setMode] = useState<FormMode>("note");
  const [isCreatingList, setIsCreatingList] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const titleInputRef = useRef<HTMLInputElement>(null);
  const noteEditorRef = useRef<MarkdownEditorHandle>(null);
  const listEditorRef = useRef<MarkdownEditorHandle>(null);

  const { data: noteTypes = [] } = useNoteTypesQuery();
  const createNoteListMutation = useCreateNoteList();
  const addNoteToListMutation = useAddNoteToList();
  const createNoteMutation = useCreateNote();

  const form = useForm<CompactNoteFormValues>({
    resolver: zodResolver(compactNoteSchema),
    defaultValues: {
      title: "",
      content: "",
      type: "todo",
      dueDate: defaultDate,
    },
  });

  const title = form.watch("title");
  const selectedType = form.watch("type");

  useEffect(() => {
    if (autoExpand !== undefined && autoExpand !== isExpanded) {
      setIsExpanded(autoExpand);
      if (autoExpand) {
        setTimeout(() => titleInputRef.current?.focus(), 100);
      }
    }
  }, [autoExpand, isExpanded]);

  useEffect(() => {
    if (defaultDate !== undefined) {
      form.setValue("dueDate", defaultDate);
    }
  }, [defaultDate, form]);

  const prevExpandedRef = useRef(isExpanded);
  useEffect(() => {
    if (prevExpandedRef.current !== isExpanded) {
      prevExpandedRef.current = isExpanded;
      onExpandChange?.(isExpanded);
    }
  }, [isExpanded, onExpandChange]);

  const classifyWithLLM = useCallback(
    async (text: string, currentType: string) => {
      if (!text.trim() || text.length < 3) return;

      try {
        const result = await chatApi.classifyNote(text);
        if (result.classification && result.classification !== currentType) {
          form.setValue("type", result.classification);
        }
      } catch (error) {
        console.error("Classification failed:", error);
      }
    },
    [form],
  );

  useEffect(() => {
    if (mode === "list") return;

    const timer = setTimeout(() => {
      const shouldClassify =
        /\b(watch|movie|film|series|show|episode)\b/i.test(title) ||
        /\b(grocery|shopping|buy|get|pick\s*up|grab|need)\b/i.test(title) ||
        /https?:\/\//.test(title);

      if (title && title.length > 3 && shouldClassify) {
        classifyWithLLM(title, selectedType);
      }
    }, 800);

    return () => clearTimeout(timer);
  }, [title, selectedType, classifyWithLLM, mode]);

  useEventListener("keydown", (e) => {
    const activeTag = document.activeElement?.tagName;
    const isInteractiveElement =
      activeTag === "INPUT" ||
      activeTag === "TEXTAREA" ||
      activeTag === "BUTTON" ||
      activeTag === "SELECT" ||
      document.activeElement?.getAttribute("role") === "button" ||
      document.activeElement?.getAttribute("contenteditable") === "true";
    const hasOpenDialog = document.querySelector(
      "[data-state='open'][role='dialog'], [data-state='open'][role='alertdialog']",
    );

    if (
      e.code === "Space" &&
      !isExpanded &&
      !isInteractiveElement &&
      !hasOpenDialog
    ) {
      e.preventDefault();
      setIsExpanded(true);
      setTimeout(() => titleInputRef.current?.focus(), 100);
    }

    if (e.key === "Escape" && isExpanded) {
      handleCancel();
    }
  });

  const handleNoteContentChange = (value: string) => {
    form.setValue("content", value);
  };

  const handleNoteTaskListDetected = useCallback(() => {
    if (mode === "note" && noteEditorRef.current) {
      const taskItems = noteEditorRef.current.getTaskItems();
      if (taskItems.length > 0) {
        setMode("list");
        setTimeout(() => {
          if (listEditorRef.current) {
            listEditorRef.current.setTaskListContent(taskItems);
          }
        }, 50);
      }
    }
  }, [mode]);

  const handleModeSwitch = (newMode: FormMode) => {
    if (newMode === mode) return;

    if (newMode === "list" && noteEditorRef.current) {
      const taskItems = noteEditorRef.current.getTaskItems();
      setMode("list");
      if (taskItems.length > 0) {
        setTimeout(() => {
          listEditorRef.current?.setTaskListContent(taskItems);
        }, 50);
      } else {
        setTimeout(() => {
          listEditorRef.current?.focus();
        }, 50);
      }
    } else if (newMode === "note") {
      setMode("note");
      form.setValue("content", "");
    }
  };

  const handleFormSubmit = async (values: CompactNoteFormValues) => {
    if (mode === "list") {
      const taskItems =
        listEditorRef.current
          ?.getTaskItems()
          .filter((item) => item.content.trim()) ?? [];

      if (taskItems.length === 0) return;

      setIsCreatingList(true);
      try {
        const listName = values.title.trim() || `${taskItems[0].content} list`;

        const list = await createNoteListMutation.mutateAsync({
          name: listName,
          kind: "LIST",
          schema: { properties: {} },
        });

        for (const item of taskItems) {
          const note = await createNoteMutation.mutateAsync({
            title: item.content,
            content: "",
            type: "todo",
          });

          await addNoteToListMutation.mutateAsync({
            listId: list.id,
            noteId: note.id,
          });
        }

        await queryClient.prefetchQuery(noteListQueryOptions(list.id));

        form.reset();
        setMode("note");
        setIsExpanded(false);

        navigate({ to: "/lists/$listId", params: { listId: list.id } });
      } catch (error) {
        console.error("Failed to create list:", error);
      } finally {
        setIsCreatingList(false);
      }
      return;
    }

    await onSubmit({
      title: values.title.trim(),
      content: values.content?.trim() || undefined,
      type: values.type,
      dueDate: values.dueDate,
    });

    form.reset();
    setMode("note");
    setIsExpanded(false);
  };

  const handleCancel = () => {
    form.reset();
    setMode("note");
    setIsExpanded(false);
  };

  const handleOpen = () => {
    setIsExpanded(true);
    setTimeout(() => titleInputRef.current?.focus(), 100);
  };

  const handleTitleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      if (title.trim()) {
        form.handleSubmit(handleFormSubmit)();
      }
      return;
    }
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (mode === "list") {
        listEditorRef.current?.focus();
      } else {
        noteEditorRef.current?.focus();
      }
    }
  };

  const taskItemCount =
    mode === "list"
      ? (listEditorRef.current?.getTaskItems().filter((i) => i.content.trim())
          .length ?? 0)
      : 0;

  const renderModeToggle = () => (
    <div className="flex items-center rounded-md bg-muted/50 p-0.5">
      <button
        type="button"
        onClick={() => handleModeSwitch("note")}
        className={cn(
          "flex items-center gap-1.5 rounded px-2.5 py-1 text-xs font-medium transition-all",
          mode === "note"
            ? "bg-background text-foreground shadow-sm"
            : "text-muted-foreground hover:text-foreground",
        )}
      >
        <FileText className="h-3.5 w-3.5" />
        Note
      </button>
      <button
        type="button"
        onClick={() => handleModeSwitch("list")}
        className={cn(
          "flex items-center gap-1.5 rounded px-2.5 py-1 text-xs font-medium transition-all",
          mode === "list"
            ? "bg-background text-foreground shadow-sm"
            : "text-muted-foreground hover:text-foreground",
        )}
      >
        <ListChecks className="h-3.5 w-3.5" />
        List
      </button>
    </div>
  );

  const renderContent = () => (
    <div className="px-3 md:px-4 pb-3 md:pb-4 space-y-3">
      {mode === "note" && (
        <FormField
          control={form.control}
          name="content"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <MarkdownEditor
                  ref={noteEditorRef}
                  content={field.value || ""}
                  onChange={handleNoteContentChange}
                  onTaskListDetected={handleNoteTaskListDetected}
                  placeholder=""
                  className="border-0 bg-muted min-h-[60px] rounded-md"
                />
              </FormControl>
            </FormItem>
          )}
        />
      )}

      {mode === "list" && (
        <MarkdownEditor
          ref={listEditorRef}
          taskListOnly
          onChange={() => {}}
          placeholder="Add an item..."
          autoFocus
          showBubbleMenu={false}
          className="border-0 bg-muted min-h-[60px] rounded-md"
        />
      )}

      <div className="flex items-center gap-2 text-sm overflow-x-auto">
        <button
          type="button"
          className="flex items-center gap-1.5 px-2 py-1 rounded-md hover:bg-muted/50 text-muted-foreground hover:text-foreground transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-primary/50"
          title="Add tags"
        >
          <Tag className="h-4 w-4" />
        </button>

        <button
          type="button"
          className="flex items-center gap-1.5 px-2 py-1 rounded-md hover:bg-muted/50 text-muted-foreground hover:text-foreground transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-primary/50"
          title="Set priority"
        >
          <Flag className="h-4 w-4" />
        </button>
      </div>

      <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-2 pt-2 border-t border-border/50">
        {mode === "note" && (
          <FormField
            control={form.control}
            name="type"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <select
                    {...field}
                    className="text-xs text-muted-foreground bg-transparent border-0 focus:outline-none focus:ring-0 cursor-pointer flex-shrink-0"
                  >
                    {noteTypes.map(
                      (type: { id: string; name: string; label: string }) => (
                        <option key={type.id} value={type.name}>
                          {type.label}
                        </option>
                      ),
                    )}
                  </select>
                </FormControl>
              </FormItem>
            )}
          />
        )}
        {mode === "list" && (
          <span className="text-xs text-muted-foreground">
            {taskItemCount} item{taskItemCount !== 1 ? "s" : ""}
          </span>
        )}

        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleCancel}
            className="h-8 text-xs flex-1 md:flex-none"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            size="sm"
            disabled={isSubmitting || isCreatingList || !title.trim()}
            className="h-8 text-xs flex-1 md:flex-none"
          >
            {isSubmitting || isCreatingList ? (
              <>
                <Loader2 className="h-3 w-3 animate-spin mr-1" />
                {mode === "list" ? "Creating..." : "Adding..."}
              </>
            ) : mode === "list" ? (
              "Create list"
            ) : (
              "Add"
            )}
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="w-full">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleFormSubmit)}>
          <AnimatePresence mode="wait">
            {!isExpanded ? (
              <motion.button
                type="button"
                onClick={handleOpen}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="w-full flex items-center gap-3 px-4 py-3 bg-card border border-border rounded-lg text-muted-foreground hover:text-foreground hover:border-primary/50 transition-colors cursor-text touch-manipulation"
              >
                <span className="flex items-center gap-3">
                  <Plus className="h-4 w-4" />
                  <span className="text-sm">Add a note...</span>
                </span>
                <span className="ml-auto text-xs text-muted-foreground/60 hidden md:block">
                  Press Space
                </span>
              </motion.button>
            ) : (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className={cn(
                  "bg-card border border-border rounded-lg overflow-hidden shadow-lg w-full",
                )}
              >
                <div className="p-3 md:p-4 space-y-3">
                  <div className="flex items-center justify-between gap-3">
                    <FormField
                      control={form.control}
                      name="title"
                      render={({ field }) => (
                        <FormItem className="flex-1">
                          <FormControl>
                            <Input
                              {...field}
                              ref={titleInputRef}
                              onKeyDown={handleTitleKeyDown}
                              placeholder={
                                mode === "list" ? "List name" : "New note"
                              }
                              className="border-0 bg-transparent p-0 h-auto text-base font-medium placeholder:text-muted-foreground focus-visible:ring-0 focus-visible:ring-offset-0"
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                    {renderModeToggle()}
                  </div>
                </div>

                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  transition={{
                    height: {
                      type: "spring",
                      bounce: 0.15,
                      duration: 0.6,
                    },
                    opacity: {
                      duration: 0.2,
                    },
                  }}
                >
                  {renderContent()}
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </form>
      </Form>
    </div>
  );
}
